(function (root, factory) {
  const api = factory();
  if (typeof module === 'object' && module.exports) module.exports = api;
  else root.FsGoogleAdsPromotion = api;
})(typeof window === 'undefined' ? globalThis : window, function () {
  'use strict';

  const DEFAULT_CODES = Object.freeze(['EXTRA5', 'ADS15GS', 'ADS30GS', 'ADS25-1', 'SGNUP20-DF6W']);
  const CLICK_PARAMETERS = new Set(['gclid', 'gbraid', 'wbraid']);
  const META_SOURCES = new Set(['meta', 'facebook', 'fb', 'instagram', 'ig', 'messenger', 'msg', 'threads', 'th', 'an', 'audience_network']);
  const META_DOMAINS = ['facebook.com', 'fb.com', 'fb.me', 'instagram.com', 'messenger.com', 'threads.net', 'threads.com'];
  const PINTEREST_DOMAINS = [
    'pinterest.com', 'pin.it', 'pinterest.ca', 'pinterest.co.uk', 'pinterest.com.au', 'pinterest.co.nz',
    'pinterest.de', 'pinterest.fr', 'pinterest.es', 'pinterest.it', 'pinterest.pt', 'pinterest.nl',
    'pinterest.be', 'pinterest.at', 'pinterest.ch', 'pinterest.ie', 'pinterest.dk', 'pinterest.se',
    'pinterest.no', 'pinterest.fi', 'pinterest.jp', 'pinterest.co.kr', 'pinterest.in', 'pinterest.co.in',
    'pinterest.com.mx', 'pinterest.com.br', 'pinterest.cl', 'pinterest.com.tr', 'pinterest.ph', 'pinterest.id'
  ];
  const SESSION_PATTERN = /^[a-z0-9-]{16,80}$/i;
  const SESSION_DURATION_MS = 2 * 60 * 60 * 1000;

  function readActivation(value, now = Date.now()) {
    if (typeof value !== 'string') return null;
    const [version, id, start, end, extra] = value.split('.');
    const startedAt = Number(start), expiresAt = Number(end);
    if (version !== 'v2' || !SESSION_PATTERN.test(id || '') || extra !== undefined ||
      !/^\d+$/.test(start || '') || !/^\d+$/.test(end || '') ||
      !Number.isSafeInteger(startedAt) || !Number.isSafeInteger(expiresAt) ||
      expiresAt - startedAt !== SESSION_DURATION_MS || startedAt > now || expiresAt <= now) return null;
    return {id, startedAt, expiresAt};
  }

  function codeKey(value) {
    return String(value || '').trim().toUpperCase();
  }

  function uniqueCodes(values) {
    const found = new Set();
    return values.filter(value => typeof value === 'string').map(value => value.trim()).filter(value => {
      const key = codeKey(value);
      if (!key || value.includes(',') || found.has(key)) return false;
      found.add(key);
      return true;
    });
  }

  function sameCodes(left, right) {
    return JSON.stringify(left.map(codeKey).sort()) === JSON.stringify(right.map(codeKey).sort());
  }

  function isGoogleAdsLanding(search) {
    const parameters = Array.from(new URLSearchParams(search).entries());
    if (parameters.some(([name, value]) =>
      CLICK_PARAMETERS.has(name.toLowerCase()) && value.trim().length > 0
    )) return true;
    const values = new Map(parameters.map(([name, value]) => [name.toLowerCase(), value.trim().toLowerCase()]));
    return values.get('utm_source') === 'google' && ['cpc', 'ppc', 'paid_search'].includes(values.get('utm_medium'));
  }

  function channelForHost(value) {
    const host = value.toLowerCase().replace(/\.$/, '');
    const matches = domain => host === domain || host.endsWith('.' + domain);
    if (META_DOMAINS.some(matches)) return 'meta';
    if (PINTEREST_DOMAINS.some(matches)) return 'pinterest';
    return null;
  }

  function channelForReferrer(value) {
    if (typeof value !== 'string' || !value.trim()) return null;
    try {
      const url = new URL(value);
      return ['http:', 'https:'].includes(url.protocol) ? channelForHost(url.hostname) : null;
    } catch (_) {
      return null;
    }
  }

  function detectLandingSource(search, referrer) {
    const parameters = Array.from(new URLSearchParams(search).entries());
    if (isGoogleAdsLanding(search)) return 'google_ads';
    for (const [name, value] of parameters) {
      if (!value.trim()) continue;
      if (name.toLowerCase() === 'fbclid') return 'meta';
      if (name.toLowerCase() === 'epik') return 'pinterest';
      if (name.toLowerCase() === 'utm_source') {
        const source = value.trim().toLowerCase();
        if (META_SOURCES.has(source)) return 'meta';
        if (source === 'pinterest') return 'pinterest';
        const channel = channelForHost(source) || channelForReferrer(source);
        if (channel) return channel;
      }
    }
    return channelForReferrer(referrer);
  }

  function captureSession(environment) {
    const now = (environment.now || Date.now)();
    let activation = readActivation(environment.read(), now);
    if (!activation && environment.allowNew !== false && detectLandingSource(environment.search, environment.referrer)) {
      const candidate = environment.createId();
      if (!SESSION_PATTERN.test(candidate)) throw new Error('Invalid promotion session identifier');
      const expiresAt = now + SESSION_DURATION_MS;
      const value = ['v2', candidate, now, expiresAt].join('.');
      environment.write(value, {expiresAt});
      activation = environment.read() === value ? readActivation(value, now) : null;
    }
    return activation ? activation.id : null;
  }

  // Absolute deadlines also cover background timers that fire late or a restored browser.
  function createExpiryWatcher(dependencies) {
    let tracked = null, timer = null, stopped = false;
    function check() {
      dependencies.clearTimeout(timer);
      timer = null;
      if (stopped) return;
      const active = dependencies.readActivation();
      if (active) {
        tracked = active;
        timer = dependencies.setTimeout(check, Math.max(1, active.expiresAt - dependencies.now()));
      } else if (tracked) {
        tracked = null;
        dependencies.onExpire();
      }
    }
    return {check, stop() {stopped = true; dependencies.clearTimeout(timer);}};
  }

  function cartIdentity(cart) {
    if (!cart || typeof cart.token !== 'string' || !Array.isArray(cart.items) ||
      (cart.discount_codes != null && !Array.isArray(cart.discount_codes))) {
      throw new Error('Unexpected Shopify cart response');
    }
    return cart.token.split('?')[0];
  }

  function discountEntries(cart) {
    return Array.isArray(cart.discount_codes) ? cart.discount_codes : [];
  }

  function validRecord(record) {
    return record && record.version === 1 && typeof record.cart === 'string' &&
      typeof record.session === 'string' && SESSION_PATTERN.test(record.session) &&
      Array.isArray(record.codes) && record.codes.every(code => typeof code === 'string');
  }

  function planCodes(cart, session, record, targetCodes) {
    const identity = cartIdentity(cart);
    const current = uniqueCodes(discountEntries(cart).map(entry => entry.code));
    const remembered = validRecord(record) && record.cart === identity ? record : null;
    const previouslyOwned = uniqueCodes(remembered ? remembered.codes : []);
    const ownedKeys = new Set(previouslyOwned.map(codeKey));
    if (!session) {
      return {
        identity, current, desired: current.filter(code => !ownedKeys.has(codeKey(code))),
        owned: [], previouslyOwned, remembered, active: false, conflict: false
      };
    }
    const targets = uniqueCodes(targetCodes);
    const targetKeys = new Set(targets.map(codeKey));
    const currentKeys = new Set(current.map(codeKey));
    const retained = current.filter(code => !ownedKeys.has(codeKey(code)) || targetKeys.has(codeKey(code)));
    const desired = uniqueCodes([...retained, ...targets]);
    const owned = uniqueCodes([
      ...previouslyOwned.filter(code => targetKeys.has(codeKey(code))),
      ...targets.filter(code => !currentKeys.has(codeKey(code)))
    ]);
    return {identity, current, desired, owned, previouslyOwned, remembered, active: true, conflict: desired.length > 5};
  }

  function attemptIdentity(cart, session, targets, previouslyOwned) {
    const managed = new Set([...targets, ...previouslyOwned].map(codeKey));
    const present = uniqueCodes(discountEntries(cart).map(entry => entry.code)).map(codeKey);
    return JSON.stringify([
      cartIdentity(cart), session, targets.map(codeKey).sort(), cart.currency,
      cart.items.map(item => [item.key, item.variant_id, item.quantity, item.original_price, item.properties,
        item.selling_plan_allocation && item.selling_plan_allocation.selling_plan.id]),
      present.filter(code => !managed.has(code)).sort(),
      present.filter(code => managed.has(code)).sort()
    ]);
  }

  function outcome(status, cart, targets, updated, ownership) {
    const applicable = cart ? uniqueCodes(discountEntries(cart).filter(entry => entry.applicable === true).map(entry => entry.code)) : [];
    const applicableKeys = new Set(applicable.map(codeKey));
    return {
      status, updated: Boolean(updated), cart,
      ownedCodeCount: ownership ? ownership.owned.length : null,
      cartCodeCount: cart ? discountEntries(cart).length : null,
      applicableCodes: applicable,
      notApplicableCodes: targets.filter(code => !applicableKeys.has(codeKey(code)))
    };
  }

  function createController(options, dependencies) {
    const targets = uniqueCodes(options.codes || DEFAULT_CODES);
    let queue = Promise.resolve();
    let lastAttempt = null;
    let stopped = false;

    async function reconcileNow(retries = 0, alreadyUpdated = false) {
      if (retries > 3) throw new Error('Promotion eligibility changed repeatedly');
      if (!options.enabled || stopped) return outcome('disabled', null, targets, false);
      const session = dependencies.getSession();
      const restartIfChanged = () => dependencies.getSession() !== session;
      let record;
      try {
        record = await dependencies.records.read();
      } catch (_) {
        return outcome('storage_unavailable', null, targets, false);
      }
      if (!validRecord(record)) record = null;
      if (restartIfChanged()) return reconcileNow(retries + 1, alreadyUpdated);
      if (!session && !record && !dependencies.readInactiveCart?.()) return outcome('inactive', null, targets, alreadyUpdated);
      const cart = await dependencies.readCart();
      if (restartIfChanged()) return reconcileNow(retries + 1, alreadyUpdated);
      const plan = planCodes(cart, session, record, targets);
      if (stopped) return outcome('disabled', cart, targets, false);
      if (plan.conflict) return outcome('code_limit_conflict', cart, targets, false);
      if (!plan.active && !plan.remembered) {
        await dependencies.records.clear();
        return outcome('inactive', cart, targets, alreadyUpdated);
      }
      const attempt = attemptIdentity(cart, session, targets, plan.previouslyOwned);
      const needsUpdate = !sameCodes(plan.current, plan.desired);
      const nextRecord = plan.owned.length ? {version: 1, cart: plan.identity, session, codes: plan.owned} : null;
      if (!needsUpdate || (plan.active && attempt === lastAttempt)) {
        if (nextRecord) await dependencies.records.write(nextRecord);
        else await dependencies.records.clear();
        if (restartIfChanged()) return reconcileNow(retries + 1, alreadyUpdated);
        return outcome(plan.active ? 'active' : 'inactive', cart, targets, alreadyUpdated, plan);
      }
      if (plan.active) {
        try {
          await dependencies.records.write({version: 1, cart: plan.identity, session,
            codes: uniqueCodes([...plan.previouslyOwned, ...plan.owned])});
        } catch (_) {
          return outcome('storage_unavailable', cart, targets, false);
        }
      }
      if (stopped) return outcome('disabled', cart, targets, false);
      if (restartIfChanged()) return reconcileNow(retries + 1, alreadyUpdated);
      const updated = await dependencies.updateCart({discount: plan.desired.join(',')});
      if (cartIdentity(updated) !== plan.identity) throw new Error('Shopify cart changed during promotion update');
      if (restartIfChanged()) return reconcileNow(retries + 1, true);
      if (!plan.active) {
        const remaining = new Set(discountEntries(updated).map(entry => codeKey(entry.code)));
        if (plan.previouslyOwned.some(code => remaining.has(codeKey(code)))) {
          return outcome('cleanup_incomplete', updated, targets, true, {owned: plan.previouslyOwned});
        }
      }
      if (nextRecord) await dependencies.records.write(nextRecord);
      else await dependencies.records.clear();
      if (restartIfChanged()) return reconcileNow(retries + 1, true);
      lastAttempt = attemptIdentity(updated, session, targets, plan.previouslyOwned);
      return outcome(plan.active ? 'active' : 'inactive', updated, targets, true, plan);
    }

    function reconcile() {
      const execute = () => dependencies.withLock ? dependencies.withLock(() => reconcileNow()) : reconcileNow();
      const task = queue.catch(() => undefined).then(execute);
      queue = task;
      return task;
    }

    return {reconcile, stop() {stopped = true;}};
  }

  function mount(configuration) {
    const config = configuration || {};
    if (!config.enabled) return null;
    const win = window;
    if (win.fsGoogleAdsPromotion) return win.fsGoogleAdsPromotion;
    const doc = win.document;
    const namespace = /^\d+$/.test(String(config.themeId || '')) ? ':' + config.themeId : '';
    const cookieName = 'fs_google_ads_session' + namespace.replace(':', '_');
    const storageKey = 'fs:google-ads-promotion:ownership:v1' + namespace;
    const rootPath = (config.rootPath || (win.Shopify && win.Shopify.routes && win.Shopify.routes.root) || '/').replace(/\/?$/, '/');
    if (!/^\/(?!\/)[^?#]*\/$/.test(rootPath) && rootPath !== '/') throw new Error('Invalid Shopify locale root');
    let disposed = false;
    let scheduled = null;
    let readInactiveCart = false;

    const readSession = () => {
      const entry = doc.cookie.split(';').map(value => value.trim()).find(value => value.startsWith(cookieName + '='));
      return entry ? entry.slice(cookieName.length + 1) : null;
    };
    const getActivation = () => readActivation(readSession());
    const navigation = win.performance?.getEntriesByType('navigation')[0]?.type;

    captureSession({
      search: win.location.search,
      referrer: doc.referrer,
      allowNew: !['reload', 'back_forward'].includes(navigation),
      read: readSession,
      write: (value, deadline) => {doc.cookie = cookieName + '=' + value + '; Max-Age=7200; Expires=' +
        new Date(deadline.expiresAt).toUTCString() + '; Path=/; SameSite=Lax' + (win.location.protocol === 'https:' ? '; Secure' : '');},
      createId: () => win.crypto.randomUUID()
    });

    async function request(path, body) {
      const abort = new AbortController();
      const timer = win.setTimeout(() => abort.abort(), 10000);
      try {
        const response = await win.fetch(rootPath + path, {
          method: body ? 'POST' : 'GET', credentials: 'same-origin', cache: 'no-store', redirect: 'error',
          headers: {'Accept': 'application/json', ...(body ? {'Content-Type': 'application/json'} : {})},
          ...(body ? {body: JSON.stringify(body)} : {}), signal: abort.signal
        });
        if (!response.ok) throw new Error('Shopify promotion request failed: ' + response.status);
        return await response.json();
      } finally {
        win.clearTimeout(timer);
      }
    }

    const controller = createController({enabled: true, codes: config.codes || DEFAULT_CODES}, {
      getSession: () => getActivation()?.id || null,
      readInactiveCart: () => readInactiveCart,
      records: {
        read: () => {const value = win.localStorage.getItem(storageKey); try {return value ? JSON.parse(value) : null;} catch (_) {return null;}},
        write: record => win.localStorage.setItem(storageKey, JSON.stringify(record)),
        clear: () => win.localStorage.removeItem(storageKey)
      },
      readCart: () => request('cart.js'),
      updateCart: body => request('cart/update.js', body),
      withLock: config.withCartLock || (action => win.navigator.locks ? win.navigator.locks.request('fs-google-ads-promotion', action) : action())
    });
    const watcher = createExpiryWatcher({
      readActivation: getActivation, now: Date.now,
      setTimeout: (action, delay) => win.setTimeout(action, delay), clearTimeout: timer => win.clearTimeout(timer),
      onExpire() {
        readInactiveCart = true;
        instance.latest = outcome('expiry_pending', null, [], false);
        doc.dispatchEvent(new CustomEvent('fs:promotion-expired'));
        reconcile();
      }
    });

    function emit(result) {
      if (disposed) return result;
      const refreshExpiredCart = readInactiveCart && result.status === 'inactive' && result.cart;
      if (result.status === 'inactive' && result.cart) readInactiveCart = false;
      instance.latest = result;
      const diagnostic = doc.documentElement;
      diagnostic?.setAttribute?.('data-fs-promotion-status', result.status);
      diagnostic?.setAttribute?.('data-fs-promotion-owned-code-count', String(result.ownedCodeCount ?? ''));
      diagnostic?.setAttribute?.('data-fs-promotion-cart-code-count', String(result.cartCodeCount ?? ''));
      doc.dispatchEvent(new CustomEvent('fs:google-ads-promotion', {detail: {
        status: result.status, updated: result.updated,
        ownedCodeCount: result.ownedCodeCount, cartCodeCount: result.cartCodeCount,
        applicableCodes: result.applicableCodes, notApplicableCodes: result.notApplicableCodes
      }}));
      if (result.updated || refreshExpiredCart) doc.dispatchEvent(new CustomEvent('cart:build'));
      return result;
    }

    function reconcile() {
      watcher.check();
      return controller.reconcile().then(emit).catch(() => {
        const failed = outcome('request_failed', null, [], false);
        emit(failed);
        return failed;
      });
    }

    function schedule() {
      if (disposed || scheduled !== null) return;
      scheduled = win.setTimeout(() => {scheduled = null; reconcile();}, 30);
    }

    const events = config.cartEvents || ['ajaxProduct:added', 'cart:updated', 'cart:quantity.cart-page'];
    function resume() {watcher.check(); schedule();}
    function storageChanged(event) {if (event.key === storageKey) resume();}
    events.forEach(event => doc.addEventListener(event, schedule));
    doc.addEventListener('visibilitychange', resume);
    win.addEventListener('pageshow', resume);
    win.addEventListener('focus', resume);
    win.addEventListener('storage', storageChanged);
    const instance = {
      reconcile,
      getActivation,
      ready: null,
      latest: null,
      destroy() {
        disposed = true;
        controller.stop();
        watcher.stop();
        win.clearTimeout(scheduled);
        events.forEach(event => doc.removeEventListener(event, schedule));
        doc.removeEventListener('visibilitychange', resume);
        win.removeEventListener('pageshow', resume);
        win.removeEventListener('focus', resume);
        win.removeEventListener('storage', storageChanged);
        delete win.fsGoogleAdsPromotion;
      }
    };
    win.fsGoogleAdsPromotion = instance;
    instance.ready = reconcile();
    return instance;
  }

  return Object.freeze({DEFAULT_CODES, SESSION_DURATION_MS, readActivation, createExpiryWatcher,
    isGoogleAdsLanding, detectLandingSource, captureSession, planCodes, createController, mount});
});

;
(function (root, factory) {
  const api = factory();
  if (typeof module === 'object' && module.exports) module.exports = api;
  else root.FsNativePromotionPrices = api;
})(typeof window === 'undefined' ? globalThis : window, function () {
  'use strict';

  const QUERY = 'mutation PromotionPriceQuote($input: CartInput!, $country: CountryCode!) @inContext(country: $country) { cartCreate(input: $input) { cart { discountCodes { code applicable } lines(first: 1) { nodes { quantity discountAllocations { __typename discountedAmount { amount currencyCode } ... on CartAutomaticDiscountAllocation { title } ... on CartCodeDiscountAllocation { code } } merchandise { ... on ProductVariant { id price { amount currencyCode } } } cost { amountPerQuantity { amount currencyCode } totalAmount { amount currencyCode } } } } } userErrors { field message code } } }';

  function minorUnits(value) {
    if (typeof value !== 'string' || !/^\d+(?:\.\d{1,2})?$/.test(value)) throw Error('Unsupported money value');
    const [whole, fraction = ''] = value.split('.');
    const amount = Number(whole) * 100 + Number(fraction.padEnd(2, '0'));
    if (!Number.isSafeInteger(amount)) throw Error('Unsafe money value');
    return amount;
  }

  function hasSellingPlan(value) {
    return typeof value === 'string' && /^\d+$/.test(value) && Number(value) > 0;
  }

  function readQuote(payload, request) {
    if (payload.errors?.length || payload.data?.cartCreate?.userErrors?.length) throw Error('Shopify rejected the price quote');
    const lines = payload.data?.cartCreate?.cart?.lines?.nodes;
    if (!Array.isArray(lines) || lines.length !== 1) throw Error('Missing price quote');
    const line = lines[0];
    if (line.quantity !== 1 || line.merchandise?.id !== request.variantId) throw Error('Price quote does not match variant');
    const money = line.cost?.totalAmount;
    const catalog = line.merchandise?.price;
    if (money?.currencyCode !== request.currency || catalog?.currencyCode !== request.currency) throw Error('Price quote does not match currency');
    if (minorUnits(catalog.amount) !== request.catalogPrice) throw Error('Price quote does not match catalog context');
    const price = minorUnits(money.amount);
    if (price > request.catalogPrice) throw Error('Price quote exceeds catalog price');
    return {price, currency: request.currency, variantId: request.variantId, allocations: line.discountAllocations || []};
  }

  function createClient(dependencies) {
    const pending = new Map();
    const cache = new Map();
    const clock = dependencies.now || Date.now;
    return {
      async quote(request) {
        if (request.authenticated) throw Error('A customer-aware quote provider is required');
        if (!/^gid:\/\/shopify\/ProductVariant\/\d+$/.test(request.variantId) ||
          !/^[A-Z]{2}$/.test(request.country) || !/^[A-Z]{3}$/.test(request.currency) ||
          !Number.isSafeInteger(request.catalogPrice) || request.catalogPrice < 0 ||
          !Array.isArray(request.codes) || request.codes.length > 5) throw Error('Invalid quote request');
        const key = JSON.stringify([request.variantId, request.country, request.currency, request.catalogPrice,
          request.codes.map(code => code.toUpperCase()).sort(), request.scope]);
        const cached = cache.get(key);
        if (cached && cached.expires > clock()) return cached.value;
        if (pending.has(key)) return pending.get(key);
        const task = (async () => {
          const payload = await dependencies.send({query: QUERY, variables: {country: request.country, input: {
            buyerIdentity: {countryCode: request.country}, discountCodes: request.codes,
            lines: [{merchandiseId: request.variantId, quantity: 1}]
          }}});
          const value = readQuote(payload, request);
          if (cache.size >= 64) cache.delete(cache.keys().next().value);
          cache.set(key, {value, expires: clock() + 120000});
          return value;
        })();
        pending.set(key, task);
        try {return await task;} finally {pending.delete(key);}
      }
    };
  }

  function mount(configuration, promotion) {
    const win = window;
    const doc = win.document;
    const states = new Map();
    let confirmedCodes = null;
    const client = createClient({send: async body => {
      const abort = new AbortController();
      const timer = win.setTimeout(() => abort.abort(), 8000);
      try {
        const response = await win.fetch('/api/2026-10/graphql.json', {
          method: 'POST', credentials: 'same-origin', cache: 'no-store', redirect: 'error',
          headers: {'Content-Type': 'application/json', 'Accept': 'application/json'},
          body: JSON.stringify(body), signal: abort.signal
        });
        if (!response.ok) throw Error('Price quote request failed');
        return await response.json();
      } finally {win.clearTimeout(timer);}
    }});

    function emit(state, status, started) {
      state.root.dataset.fsPromotionPrice = status;
      const activation = promotion.getActivation?.();
      state.root.dataset.fsPromotionStartedAt = String(activation?.startedAt || '');
      state.root.dataset.fsPromotionExpiresAt = String(activation?.expiresAt || '');
      state.root.dataset.fsPromotionQuoteMs = String(Math.round(performance.now() - started));
      state.root.dataset.fsPromotionNavigationMs = String(Math.round(performance.now()));
      if (status === 'verified' && state.root.dataset.fsPromotionFirstVariant !== String(state.variant.id)) {
        state.root.dataset.fsPromotionFirstVariant = String(state.variant.id);
        state.root.dataset.fsPromotionFirstVerifiedMs = String(Math.round(performance.now()));
      }
      doc.dispatchEvent(new CustomEvent('fs:native-promotion-price', {detail: {
        status, variantId: String(state.variant.id), milliseconds: Math.round(performance.now() - started)
      }}));
    }

    function format(price) {
      return new Intl.NumberFormat(doc.documentElement.lang || 'en-US', {style: 'currency', currency: configuration.currency}).format(price / 100);
    }

    function clear(state) {
      state.verifiedContext = null;
      const price = state.root.querySelector('.product-block--price [data-product-price]');
      if (!price || !state.original) return;
      price.innerHTML = state.original.price;
      price.className = state.original.className;
      const savings = state.root.querySelector('[data-save-price]');
      if (savings && state.original.savingsClass != null) savings.className = state.original.savingsClass;
      const compare = state.root.querySelector('[data-product-price-wrap]');
      if (compare && state.original.compareClass != null) compare.className = state.original.compareClass;
      state.original = null;
      if (state.note) state.note.remove();
      state.note = null;
    }

    function show(state, quote) {
      const block = state.root.querySelector('.product-block--price');
      const price = block?.querySelector('[data-product-price]');
      if (!price) return;
      const savings = block.querySelector('[data-save-price]');
      const compare = block.querySelector('[data-product-price-wrap]');
      if (!state.original) state.original = {price: price.innerHTML, className: price.className,
        savingsClass: savings?.className, compareClass: compare?.className};
      const reference = Math.max(Number(state.variant.compare_at_price || 0), state.variant.price);
      const originalPrice = doc.createElement('s');
      originalPrice.className = 'product__price product__price--compare';
      originalPrice.textContent = format(reference);
      const saleLabel = doc.createElement('span');
      saleLabel.className = 'visually-hidden';
      saleLabel.textContent = configuration.saleLabel;
      const visual = doc.createElement('span');
      visual.textContent = format(quote.price);
      price.replaceChildren(originalPrice, saleLabel, visual);
      price.classList.add('on-sale');
      savings?.classList.add('hide');
      compare?.classList.add('hide');
      if (!state.note) {
        state.note = doc.createElement('span');
        state.note.className = 'product__price-savings';
        state.note.setAttribute('aria-live', 'polite');
        block.appendChild(state.note);
      }
      state.note.textContent = configuration.savePattern.replace('[saved_amount]', format(reference - quote.price));
    }

    function quoteRequest(variant, codes, status, activation) {
      return client.quote({
        variantId: 'gid://shopify/ProductVariant/' + variant.id, catalogPrice: variant.price,
        country: configuration.country, currency: configuration.currency, codes,
        authenticated: configuration.authenticated, scope: configuration.themeId + ':' + status + ':' + (activation?.id || '')
      });
    }

    async function quoteVariant(variant, confirmContext) {
      const result = promotion.latest || await promotion.ready;
      if (['request_failed', 'storage_unavailable', 'cleanup_incomplete', 'expiry_pending'].includes(result.status)) throw Error('Cart promotion state unavailable');
      const activation = promotion.getActivation?.();
      if (!activation || !['active', 'code_limit_conflict'].includes(result.status)) throw Error('Promotion inactive');
      if (Array.isArray(result.cart?.discount_codes)) confirmedCodes = result.cart.discount_codes.map(entry => typeof entry === 'string' ? entry : entry.code);
      const codes = confirmedCodes ?? configuration.existingCodes ?? [];
      const contextKey = JSON.stringify([variant.id, variant.price, configuration.country, configuration.currency,
        codes.map(code => code.toUpperCase()).sort(), result.status, activation?.id || '']);
      confirmContext?.(contextKey);
      const quote = await quoteRequest(variant, codes, result.status, activation);
      if (promotion.getActivation && promotion.getActivation()?.id !== activation?.id) throw Error('Promotion eligibility changed');
      return {...quote, contextKey};
    }

    async function refresh(state) {
      const started = performance.now();
      const generation = ++state.generation;
      const variant = state.variant;
      if (!promotion.getActivation?.()) {
        clear(state);
        emit(state, 'inactive', started);
        return;
      }
      state.form = state.root.querySelector('form.product-single__form');
      const planFields = Array.from(state.form?.elements || []).filter(field => field.name === 'selling_plan' &&
        (!['radio', 'checkbox'].includes(field.type) || field.checked));
      const context = configuration.authenticated ? 'customer_identity_required' :
        !state.form ? 'product_form_unavailable' :
        variant.unit_price_measurement ? 'unit_price_context_required' :
        planFields.some(field => hasSellingPlan(field.value)) ? 'selling_plan_context_required' :
        null;
      if (context) {
        clear(state);
        emit(state, context, started);
        return;
      }
      try {
        const quote = await quoteVariant(variant, key => {
          if (generation === state.generation && state.verifiedContext !== key) clear(state);
        });
        if (generation !== state.generation) return;
        clear(state);
        if (quote.price < variant.price) show(state, quote);
        state.verifiedContext = quote.contextKey;
        emit(state, 'verified', started);
      } catch (_) {
        if (generation === state.generation) {clear(state); emit(state, 'unavailable', started);}
      }
    }

    function bind(root) {
      if (states.has(root)) return;
      const data = root.querySelector('[data-fs-native-promotion-product]');
      if (!data) return;
      if (doc.readyState === 'loading' && (!root.querySelector('.product-block--price [data-product-price]') ||
        !root.querySelector('form.product-single__form [name="add"]'))) return;
      let variant;
      try {variant = JSON.parse(data.textContent);} catch (_) {return;}
      const state = {root, form: root.querySelector('form.product-single__form'), variant, generation: 0, original: null, note: null};
      states.set(root, state);
      root.addEventListener('variantChange', event => {
        if (!event.detail?.variant) {++state.generation; clear(state); return;}
        clear(state);
        state.variant = event.detail.variant;
        win.queueMicrotask(() => refresh(state));
      });
      const purchaseChanged = event => {
        if (event.target.form === root.querySelector('form.product-single__form') &&
          event.target.matches('[name="selling_plan"]')) refresh(state);
      };
      root.addEventListener('change', purchaseChanged);
      root.addEventListener('input', purchaseChanged);
      refresh(state);
    }
    function scan() {Array.from(doc.querySelectorAll('product-component')).forEach(bind);}
    const initial = configuration.initialVariant;
    if (initial && !configuration.authenticated && !initial.unit_price_measurement && promotion.getActivation?.()) {
      const activation = promotion.getActivation?.();
      if (activation && configuration.promotionCodes) {
        const codes = [...new Set([...(configuration.existingCodes || []), ...configuration.promotionCodes].map(code => code.toUpperCase()))];
        if (codes.length <= 5) quoteRequest(initial, codes, 'active', activation).catch(() => undefined);
      }
      quoteVariant(initial).catch(() => undefined);
    }
    if (doc.readyState !== 'loading' || initial) scan();
    if (doc.readyState === 'loading') {
      const observer = initial && win.MutationObserver ? new win.MutationObserver(scan) : null;
      observer?.observe(doc.documentElement, {childList: true, subtree: true});
      doc.addEventListener('DOMContentLoaded', () => {observer?.disconnect(); scan();}, {once: true});
    }
    doc.addEventListener('fs:google-ads-promotion', () => states.forEach(refresh));
    doc.addEventListener('fs:promotion-expired', () => states.forEach(state => {
      ++state.generation;
      clear(state);
      state.root.dataset.fsPromotionPrice = 'expiry_pending';
    }));
    return {refresh() {states.forEach(refresh);}};
  }

  return Object.freeze({QUERY, minorUnits, hasSellingPlan, readQuote, createClient, mount});
});

;
(function () {
  'use strict';
  let started = false;
  function start() {
  if (started) return;
  const configNode = document.getElementById('FsNativePromotionConfig');
  if (!configNode || !window.FsGoogleAdsPromotion || !window.FsNativePromotionPrices) return;
  let configuration;
  try {configuration = JSON.parse(configNode.textContent);} catch (_) {return;}
  if (!configuration.enabled || (configuration.reviewOnly && (configuration.themeRole || window.Shopify?.theme?.role) !== 'unpublished')) return;
  started = true;
  document.documentElement?.setAttribute?.('data-fs-promotion-boot-ms', String(Math.round(window.performance?.now?.() || 0)));
  let queue = Promise.resolve();
  const withCartLock = action => {
    const task = queue.catch(() => undefined).then(() => window.navigator.locks ? window.navigator.locks.request('fs-shopify-cart-discounts', action) : action());
    queue = task;
    return task;
  };
  window.FsNativePromotionCoordinator = {withCartLock};
  const promotion = window.FsGoogleAdsPromotion.mount({enabled: true, themeId: configuration.themeId, rootPath: configuration.rootPath, withCartLock});
  promotion.ready?.then(result => {
    if (result.updated && document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => {
      document.dispatchEvent(new CustomEvent('cart:build'));
    }, {once: true});
  });
  window.fsNativePromotionPrices = window.FsNativePromotionPrices.mount({...configuration, promotionCodes: window.FsGoogleAdsPromotion.DEFAULT_CODES}, promotion);
  const bypass = new WeakSet();
  let pending = false;
  let replaying = false;
  async function proceed(target, replay) {
    if (pending) return;
    pending = true;
    target.setAttribute('aria-busy', 'true');
    try {
      const result = await promotion.reconcile();
      if (['request_failed', 'storage_unavailable', 'cleanup_incomplete'].includes(result.status)) throw Error('Promotion not confirmed');
      const current = target.id ? document.getElementById(target.id) : target;
      if (!current?.isConnected || current.disabled) throw Error('Checkout control changed');
      bypass.add(current);
      replaying = true;
      try {replay(current);} finally {replaying = false;}
    } catch (_) {
      let error = target.parentElement.querySelector('[data-fs-promotion-error]');
      if (!error) {
        error = document.createElement('p');
        error.setAttribute('data-fs-promotion-error', '');
        error.setAttribute('role', 'alert');
        target.after(error);
      }
      error.textContent = configuration.cartError;
    } finally {pending = false; target.removeAttribute('aria-busy');}
  }
  document.addEventListener('click', event => {
    const target = event.target.closest('[name="checkout"], [data-fs-checkout], .cart__checkout, #nvd-with-checkout-btn, #nvd-without-checkout-btn, .shopify-payment-button__button');
    if (!target || target.disabled) return;
    if (bypass.delete(target) || replaying) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    proceed(target, current => current.click());
  }, true);
  document.addEventListener('submit', event => {
    const form = event.target;
    if (!form.matches('form[action$="/cart"]') || !event.submitter?.matches('[name="checkout"],.cart__checkout')) return;
    if (bypass.delete(form) || replaying) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    proceed(form, current => {
      const submitter = event.submitter.isConnected && event.submitter.form === current ? event.submitter :
        current.querySelector('[name="checkout"],.cart__checkout');
      if (!submitter || submitter.disabled) throw Error('Checkout submitter changed');
      current.requestSubmit(submitter);
    });
  }, true);
  }
  start();
  if (!started && document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, {once: true});
})();
